import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, DollarSign, Copy, Calculator, AlertTriangle } from 'lucide-react';
import { TPSLCalculatorService } from '@/services/TPSLCalculatorService';
import { toast } from 'sonner';

interface PositionResults {
  symbol: string;
  currentPrice: number;
  optimalTvPositionSize: number;
  actualRiskUsd: number;
  actualProfitUsd: number;
  tpPrice: number;
  slPrice: number;
  riskRewardRatio: number;
  ctraderLots: number;
  positionValue: number;
  riskPercentOfAccount: number;
  priceSource: 'cache' | 'api';
}

export function PositionModeCalculator() {
  const [symbol, setSymbol] = useState('EURUSD');
  const [riskAmountUsd, setRiskAmountUsd] = useState('100');
  const [tpPercent, setTpPercent] = useState('2.0');
  const [slPercent, setSlPercent] = useState('1.0');
  const [accountBalance, setAccountBalance] = useState('10000');
  const [results, setResults] = useState<PositionResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculatePosition = async () => {
    if (!symbol || !riskAmountUsd || !tpPercent || !slPercent) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCalculating(true);
    try {
      // Get current price
      const priceResult = await TPSLCalculatorService.getCurrentPrice(symbol.toUpperCase());
      const currentPrice = priceResult.price;
      
      // Calculate optimal position size based on risk amount
      const slPercentDecimal = parseFloat(slPercent) / 100;
      const tpPercentDecimal = parseFloat(tpPercent) / 100;
      
      // Position size = Risk Amount / (SL % * Current Price)
      const optimalTvPositionSize = parseFloat(riskAmountUsd) / (slPercentDecimal * currentPrice);
      
      // Calculate actual values with optimal position size
      const positionValue = optimalTvPositionSize * currentPrice;
      const actualRiskUsd = positionValue * slPercentDecimal;
      const actualProfitUsd = positionValue * tpPercentDecimal;
      
      // Calculate prices
      const tpPrice = currentPrice * (1 + tpPercentDecimal);
      const slPrice = currentPrice * (1 - slPercentDecimal);
      
      // Calculate cTrader lot size
      const ctraderLots = positionValue / 100000;
      
      // Risk:Reward ratio
      const riskRewardRatio = actualProfitUsd / actualRiskUsd;
      
      // Risk percentage of account
      const riskPercentOfAccount = accountBalance ? (actualRiskUsd / parseFloat(accountBalance)) * 100 : 0;

      setResults({
        symbol: symbol.toUpperCase(),
        currentPrice,
        optimalTvPositionSize,
        actualRiskUsd,
        actualProfitUsd,
        tpPrice,
        slPrice,
        riskRewardRatio,
        ctraderLots,
        positionValue,
        riskPercentOfAccount,
        priceSource: priceResult.source
      });

      toast.success('Position calculation completed');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate optimal position');
    } finally {
      setIsCalculating(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Risk Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Trading Symbol</Label>
            <Input
              id="symbol"
              placeholder="EURUSD, BTCUSD, XAUUSD..."
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="riskAmountUsd">Risk Amount ($)</Label>
            <Input
              id="riskAmountUsd"
              type="number"
              step="0.01"
              placeholder="100"
              value={riskAmountUsd}
              onChange={(e) => setRiskAmountUsd(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Maximum amount you're willing to lose on this trade
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tpPercent">Take Profit %</Label>
              <Input
                id="tpPercent"
                type="number"
                step="0.1"
                placeholder="2.0"
                value={tpPercent}
                onChange={(e) => setTpPercent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slPercent">Stop Loss %</Label>
              <Input
                id="slPercent"
                type="number"
                step="0.1"
                placeholder="1.0"
                value={slPercent}
                onChange={(e) => setSlPercent(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="accountBalance">Account Balance ($) <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input
              id="accountBalance"
              type="number"
              placeholder="10000"
              value={accountBalance}
              onChange={(e) => setAccountBalance(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Used to calculate risk percentage of account
            </p>
          </div>

          <Button 
            onClick={calculatePosition} 
            disabled={isCalculating}
            className="w-full"
          >
            {isCalculating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Optimal Position
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Optimal Position
          </CardTitle>
        </CardHeader>
        <CardContent>
          {results ? (
            <div className="space-y-4">
              {/* Symbol & Price Info */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <div className="font-medium">{results.symbol}</div>
                  <div className="text-sm text-muted-foreground">
                    Current: ${results.currentPrice.toFixed(5)}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {results.priceSource === 'cache' ? 'Cached' : 'Live'}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(results.currentPrice.toString(), 'Current price')}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              {/* Optimal Position Size - Main Result */}
              <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-muted-foreground">Optimal TV Position Size</div>
                    <div className="font-mono text-xl font-bold">
                      {Math.round(results.optimalTvPositionSize).toLocaleString()} units
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(Math.round(results.optimalTvPositionSize).toString(), 'Position size')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* Risk Management Check */}
              {accountBalance && (
                <div className={`p-3 rounded-lg border ${
                  results.riskPercentOfAccount > 5 
                    ? 'bg-red-500/10 border-red-500/20' 
                    : results.riskPercentOfAccount > 2 
                      ? 'bg-yellow-500/10 border-yellow-500/20'
                      : 'bg-green-500/10 border-green-500/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {results.riskPercentOfAccount > 5 && <AlertTriangle className="h-4 w-4 text-red-500" />}
                    <div className="text-sm font-medium">
                      Risk: {results.riskPercentOfAccount.toFixed(2)}% of account
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {results.riskPercentOfAccount > 5 ? 'High Risk! Consider reducing position size' :
                     results.riskPercentOfAccount > 2 ? 'Moderate risk level' :
                     'Conservative risk level'}
                  </div>
                </div>
              )}

              {/* Profit/Loss Values */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">Potential Profit</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`$${results.actualProfitUsd.toFixed(2)}`, 'Profit USD')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium text-green-600">${results.actualProfitUsd.toFixed(2)}</div>
                </div>

                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">Risk Amount</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`$${results.actualRiskUsd.toFixed(2)}`, 'Risk USD')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium text-red-600">${results.actualRiskUsd.toFixed(2)}</div>
                </div>
              </div>

              {/* Price Levels */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">TP Price</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.tpPrice.toFixed(5), 'TP Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">${results.tpPrice.toFixed(5)}</div>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">SL Price</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.slPrice.toFixed(5), 'SL Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">${results.slPrice.toFixed(5)}</div>
                </div>
              </div>

              {/* Additional Trading Info */}
              <div className="space-y-3">
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">cTrader Lot Size</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.ctraderLots.toFixed(2), 'Lot size')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">{results.ctraderLots.toFixed(2)} lots</div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">Risk:Reward Ratio</div>
                    <div className="font-medium">1:{results.riskRewardRatio.toFixed(2)}</div>
                  </div>
                  <Badge variant={results.riskRewardRatio >= 2 ? "default" : "secondary"}>
                    {results.riskRewardRatio >= 2 ? "Good R:R" : "Low R:R"}
                  </Badge>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="text-sm text-muted-foreground">Position Value</div>
                  <div className="font-medium">${results.positionValue.toLocaleString()}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Enter your risk parameters to calculate optimal position size
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}